import type { Color } from "../../common/color.js";
import { Container, type ContainerOptions } from "../core/Container.js";
import { rectDeflate, type Insets, type Rect, type Size } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import type { ColorRef } from "../art/ArtControl.js";
import { getLook, lookContext, LookAnimator } from "../art/looks.js";

export type PanelStyle =
  /** No chrome at all; the panel is a pure grouping container. */
  | "none"
  /** A flat filled surface. */
  | "flat"
  /** Raised 3D edge (Classic) or a bordered surface (Modern). */
  | "raised"
  /** Sunken well, as used behind fields and content areas. */
  | "sunken";

export interface PanelOptions extends ContainerOptions {
  style?: PanelStyle;
  /** Overrides the style's fill; null uses the theme's window background. */
  background?: Color | null;
  padding?: Insets;
  /** Whether clicks land on the panel or fall through to whatever is behind. */
  clickThrough?: boolean;
  /** A registered look to paint instead of the style: `plate`, `card`. */
  look?: string | null;
  /** The look's color; the theme's accent when absent. */
  lookColor?: ColorRef | null;
  /** Drawn lit by a look that has a lit state. */
  lit?: boolean;
}

/**
 * A plain container with optional chrome.
 *
 * The general-purpose grouping control: toolbars, status bars, side panes and
 * the shell's taskbar are all panels. Children are positioned inside the
 * padding, and `clientRect` accounts for it, so anchors measure against the
 * padded area rather than the outer bounds.
 */
export class Panel extends Container {
  #padding: Insets;

  style: PanelStyle;
  background: Color | null;
  clickThrough: boolean;
  /** A registered look painted instead of `style`; a name nobody registered falls back to the style. */
  look: string | null;
  lookColor: ColorRef | null;
  /** Drawn lit by a look that has a lit state; nothing else reads it. */
  lit: boolean;
  #lookAnimator = new LookAnimator(this);

  constructor(options: PanelOptions = {}) {
    super({ width: 200, height: 120, ...options });
    this.style = options.style ?? "none";
    this.background = options.background ?? null;
    this.#padding = options.padding ?? { left: 0, top: 0, right: 0, bottom: 0 };
    this.clickThrough = options.clickThrough ?? false;
    this.look = options.look ?? null;
    this.lookColor = options.lookColor ?? null;
    this.lit = options.lit ?? false;
  }

  get padding(): Insets {
    return this.#padding;
  }
  set padding(v: Insets) {
    this.#padding = v;
    this.layout();
    this.invalidate();
  }

  override get clientRect(): Rect {
    return rectDeflate(this.localRect, this.#padding);
  }

  /**
   * What the children need, plus whatever the panel puts around them.
   *
   * The frame comes out of the difference between the panel's own rectangle and its client
   * rectangle, so this one method is right for a padded panel, a group box with a caption and
   * anything else that subtracts a frame by overriding `clientRect`.
   */
  override preferredSize(available: Size): Size {
    const local = this.localRect;
    const client = this.clientRect;
    const frameW = local.width - client.width;
    const frameH = local.height - client.height;
    const content = this.measureContent({
      width: Math.max(0, available.width - frameW),
      height: Math.max(0, available.height - frameH),
    });
    return { width: content.width + frameW, height: content.height + frameH };
  }

  protected override hitTestSelf(): boolean {
    return !this.clickThrough;
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const r = this.localRect;
    const look = getLook(this.look);
    if (look?.panel) {
      const ctx = lookContext(this, rc, theme, this.lookColor, this.lit);
      look.panel(ctx);
      this.#lookAnimator.sync(look.animates?.(ctx, "panel") ?? false);
      return;
    }
    const rings = this.shapeOutline();
    if (rings) {
      if (this.style !== "none" || this.background) {
        theme.drawShapedChrome(rc, rings, this.visualState(), "panel", this.background);
      }
      return;
    }
    switch (this.style) {
      case "none":
        if (this.background) rc.fillRect(r, this.background);
        break;
      case "flat":
        rc.fillRect(r, this.background ?? theme.palette.windowBg, theme.metrics.controlCornerRadius);
        break;
      case "raised":
      case "sunken": {
        theme.drawPanel(rc, r, this.style === "sunken");
        // drawPanel picks its own fill; repaint over it when one was given.
        if (this.background) {
          const inner = rectDeflate(r, { left: 2, top: 2, right: 2, bottom: 2 });
          rc.fillRect(inner, this.background);
        }
        break;
      }
    }
  }
}
